import { Link } from 'react-router'
import { type Course } from '@/lib/courses'
import { getCourseSavings, getPromoPrice } from '@/lib/pricing'
import { milestoneHeadline } from '@/lib/courseText'
import { cn } from '@/lib/utils'
import { ProgramArt } from '@/components/design/ProgramArt'
import { ToolTag } from '@/components/ToolLogo'
import { IconArrowRight, IconCalendar, IconCheck, IconStar, IconUsers } from '@/components/Icons'

const CHIP = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold'

export function CourseCard({ course, featured = false }: { course: Course; featured?: boolean }) {
  const isOpen = course.status === 'open'
  const promo = getPromoPrice(course)
  const savings = getCourseSavings(course)

  return (
    <Link
      to={`/courses/${course.slug}`}
      className={cn(
        'spotlight-card group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-[var(--surface-border)] bg-[linear-gradient(180deg,#0f1216,#0b0d10)] p-3 no-underline transition-[transform,border-color,box-shadow] duration-500 ease-[var(--ease-out-expo)] hover:-translate-y-1.5 hover:border-[var(--color-brand-accent)]/35 hover:shadow-[0_40px_90px_-40px_rgba(0,229,255,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]',
      )}
    >
      {featured && <span aria-hidden="true" className="border-beam" />}
      <ProgramArt category={course.category} tools={course.tools} />
      <div className="flex flex-1 flex-col px-3 pb-3 pt-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {course.flagship && (
            <span
              className={`${CHIP} bg-[var(--marker)]/10 text-amber-200 ring-1 ring-inset ring-[var(--marker)]/25`}
            >
              <IconStar size={11} aria-hidden="true" /> Flagship
            </span>
          )}
          {isOpen ? (
            <span
              className={`${CHIP} bg-green-400/10 text-green-200 ring-1 ring-inset ring-green-400/25`}
            >
              <span className="size-1.5 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.9)]" />
              Enrolling | ভর্তি চলছে
            </span>
          ) : (
            <span
              className={`${CHIP} bg-[var(--color-brand-accent)]/10 text-[var(--color-brand-accent-readable)] ring-1 ring-inset ring-[var(--color-brand-accent)]/25`}
            >
              Coming Soon
            </span>
          )}
          <span
            className={`${CHIP} bg-white/[0.05] font-medium text-[var(--surface-muted)] ring-1 ring-inset ring-white/10`}
          >
            {course.category}
          </span>
        </div>
        <h3 className="font-display text-[19px] font-semibold leading-snug tracking-[-0.015em] text-[var(--text-strong)] transition-colors duration-300 group-hover:text-white">
          {course.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[var(--surface-muted)]">
          {course.subtitle}
        </p>
        {course.outcomes && (
          <p className="mt-4 flex items-start gap-2 text-[13px] leading-snug text-[var(--surface-fg)]">
            <span className="mt-px flex size-4 shrink-0 items-center justify-center rounded-full bg-green-400/15 text-green-300">
              <IconCheck size={10} aria-hidden="true" />
            </span>
            <span className="line-clamp-3">
              {milestoneHeadline(course.outcomes.firstMilestone)}
            </span>
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-1.5">
          {course.tags.slice(0, 4).map((tag) => (
            <ToolTag key={tag} name={tag} />
          ))}
          {course.tags.length > 4 && (
            <span className="rounded-md border border-white/[0.07] bg-white/[0.03] px-2 py-0.5 font-code text-[11px] text-[var(--surface-muted)]">
              +{course.tags.length - 4}
            </span>
          )}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[var(--surface-muted)]">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <IconCalendar size={13} aria-hidden="true" /> {course.duration}
          </span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <IconUsers size={13} aria-hidden="true" /> {course.batchSize}
          </span>
          <span className="rounded-full bg-white/[0.05] px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ring-white/10">
            {course.level}
          </span>
        </div>
        <div aria-hidden="true" className="min-h-5 flex-1" />
        <div className="flex items-end justify-between gap-3 border-t border-white/[0.06] pt-5">
          <div>
            {promo ? (
              <>
                <span className="font-display text-2xl font-semibold tracking-[-0.02em] text-[var(--text-strong)]">
                  {promo.price}
                </span>
                <span className="ml-2 text-sm text-[var(--surface-muted)] line-through">
                  {course.price}
                </span>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-green-300">
                  {promo.label}
                  {savings && (
                    <span className="ml-1.5 normal-case tracking-normal">
                      · {savings.shortLabel}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <span className="font-display text-2xl font-semibold tracking-[-0.02em] text-[var(--text-strong)]">
                {course.price}
              </span>
            )}
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-brand-accent)]/10 px-3.5 py-2 text-sm font-semibold text-[var(--color-brand-accent)] ring-1 ring-inset ring-[var(--color-brand-accent)]/30 transition-[gap,background-color,color] duration-300 group-hover:gap-2.5 group-hover:bg-[var(--color-brand-accent)] group-hover:text-[var(--accent-ink)]">
            {isOpen ? 'See course | দেখুন' : 'View details'}
            <IconArrowRight size={14} aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  )
}
